'use client';

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  Checkbox,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  Badge,
  Pagination,
} from '@mui/material';
import {
  Search,
  CalendarToday,
  Person,
  Category,
  Image as ImageIcon,
  CheckCircle,
  RadioButtonUnchecked,
  Warning,
  CheckCircleOutline,
} from '@mui/icons-material';
import { toast } from 'sonner';
import { apiGetReviews, Review, ReviewListParams } from '../api/review.api';
import { checkImageInstagramCompatibility, InstagramAspectRatioCheck } from '../utils/imageUtils';

interface ReviewSelectorProps {
  branchId: number;
  selectedReviews: number[];
  selectedImages: string[];
  onReviewSelect: (reviewId: number) => void;
  onImageSelect: (imageUrl: string) => void;
  onReviewsLoad?: (reviews: Review[]) => void;
}

/**
 * 페이지네이션과 필터링이 포함된 리뷰 선택 컴포넌트
 * 실제 DB 데이터를 활용하여 리뷰를 보기 좋게 표시하고 선택할 수 있게 함
 */
const ReviewSelector: React.FC<ReviewSelectorProps> = ({
  branchId,
  selectedReviews,
  selectedImages,
  onReviewSelect,
  onImageSelect,
  onReviewsLoad,
}) => {
  // 상태 관리
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRangeFilter, setDateRangeFilter] = useState('30');
  
  // Instagram 호환성 체크 상태
  const [imageCompatibilityMap, setImageCompatibilityMap] = useState<Map<string, InstagramAspectRatioCheck>>(new Map());
  const [checkingImages, setCheckingImages] = useState<Set<string>>(new Set());

  // 테스트용 함수
  const testImageCompatibility = async () => {
    const testUrl = 'https://via.placeholder.com/400x300';
    console.log('🧪 테스트 이미지 호환성 체크:', testUrl);
    const result = await checkImageInstagramCompatibility(testUrl);
    console.log('🧪 테스트 결과:', result);
  };

  const itemsPerPage = 6;

  /**
   * 이미지 Instagram 호환성 체크
   */
  const checkImageCompatibility = async (imageUrl: string) => {
    if (imageCompatibilityMap.has(imageUrl) || checkingImages.has(imageUrl)) {
      return; // 이미 체크했거나 체크 중인 경우
    }

    setCheckingImages(prev => {
      const newSet = new Set(prev);
      newSet.add(imageUrl);
      return newSet;
    });

    try {
      console.log('🔍 이미지 호환성 체크 시작:', imageUrl.substring(0, 80) + '...');
      const compatibility = await checkImageInstagramCompatibility(imageUrl);
      if (compatibility) {
        console.log('✅ 호환성 체크 완료:', {
          imageUrl: imageUrl.substring(0, 80) + '...',
          isValid: compatibility.isValid,
          ratio: compatibility.ratio,
          ratioType: compatibility.ratioType
        });
        setImageCompatibilityMap(prev => {
          const newMap = new Map(prev);
          newMap.set(imageUrl, compatibility);
          return newMap;
        });
      }
    } catch (error) {
      console.error('❌ 이미지 호환성 체크 실패:', error);
    } finally {
      setCheckingImages(prev => {
        const newSet = new Set(prev);
        newSet.delete(imageUrl);
        return newSet;
      });
    }
  };

  /**
   * 리뷰 로드 시 이미지 호환성 체크
   */
  const checkReviewImagesCompatibility = async (reviews: Review[]) => {
    const imageUrls: string[] = [];
    
    reviews.forEach(review => {
      if (review.beforeImageUrl) imageUrls.push(review.beforeImageUrl);
      if (review.afterImageUrl) imageUrls.push(review.afterImageUrl);
    });

    console.log('📋 리뷰 이미지 호환성 체크 시작:', {
      reviewCount: reviews.length,
      imageCount: imageUrls.length,
      imageUrls: imageUrls.map(url => url.substring(0, 80) + '...')
    });

    if (imageUrls.length === 0) {
      console.log('⚠️ 체크할 이미지가 없습니다.');
      return;
    }

    // 배치로 호환성 체크
    imageUrls.forEach(url => {
      console.log('🔄 개별 이미지 호환성 체크 호출:', url.substring(0, 80) + '...');
      checkImageCompatibility(url);
    });
  };

  /**
   * 리뷰 데이터 로드
   */
  const fetchReviews = async (page: number = 1) => {
    if (!branchId) return;

    setLoading(true);

    try {
      const params: ReviewListParams = {
        branchId,
        page,
        limit: itemsPerPage,
        searchText: searchTerm || undefined,
        dateRange: dateRangeFilter || undefined,
      };

      console.log('📋 리뷰 로드 시작:', params);

      const response = await apiGetReviews(params);

      if (response.success) {
        setReviews(response.data);
        setTotalCount(response.total);
        setTotalPages(Math.ceil(response.total / itemsPerPage));

        // 부모 컴포넌트에 리뷰 데이터 전달
        if (onReviewsLoad) {
          onReviewsLoad(response.data);
        }

        console.log(
          `✅ 리뷰 로드 완료: ${response.data.length}개 (총 ${response.total}개)`,
        );

        // 이미지 Instagram 호환성 체크
        checkReviewImagesCompatibility(response.data);
      } else {
        throw new Error('리뷰 로드 실패');
      }
    } catch (error) {
      console.error('❌ 리뷰 로드 오류:', error);
      toast.error('리뷰 데이터를 불러오는데 실패했습니다.');
      setReviews([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 초기 로드 및 필터 변경 시 리로드
   */
  useEffect(() => {
    fetchReviews(1);
    setCurrentPage(1);
  }, [branchId, searchTerm, dateRangeFilter]);

  /**
   * 컴포넌트 마운트 시 테스트
   */
  useEffect(() => {
    console.log('🚀 ReviewSelector 컴포넌트 마운트됨');
    testImageCompatibility();
  }, []);

  /**
   * 페이지 변경
   */
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number,
  ) => {
    setCurrentPage(page);
    fetchReviews(page);
  };

  /**
   * 검색어 변경 (디바운스 적용)
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== undefined) {
        fetchReviews(1);
        setCurrentPage(1);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  /**
   * 날짜 포맷팅
   */
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  /**
   * 필터 초기화 핸들러
   */
  const handleResetFilters = () => {
    setSearchTerm('');
    setDateRangeFilter('30');
    setCurrentPage(1);
    toast.success('필터가 초기화되었습니다.');
  };

  /**
   * 이미지 선택 핸들러 - Instagram 호환성 체크 포함
   */
  const handleImageSelect = async (imageUrl: string, skipWarning: boolean = false) => {
    // 이미지 호환성 체크 (개별 클릭 시만 경고 다이얼로그 표시)
    const compatibility = imageCompatibilityMap.get(imageUrl);
    
    if (!skipWarning && compatibility && !compatibility.isValid) {
      // Instagram 호환되지 않는 이미지인 경우 경고 (개별 클릭 시만)
      const shouldProceed = window.confirm(
        `⚠️ Instagram 호환성 경고\n\n` +
        `이 이미지는 Instagram에서 지원하지 않는 화면 비율입니다.\n\n` +
        `현재 비율: ${compatibility.ratio}\n` +
        `${compatibility.recommendation}\n\n` +
        `Instagram 포스팅 시 오류가 발생할 수 있습니다.\n` +
        `그래도 선택하시겠습니까?`
      );
      
      if (!shouldProceed) {
        toast.warning('Instagram 호환되지 않는 이미지 선택이 취소되었습니다.');
        return;
      }
      
      toast.success('Instagram 비호환 이미지가 선택되었습니다. 포스팅 시 주의하세요.');
    }
    
    // 이미지 선택 처리
    onImageSelect(imageUrl);
  };

  /**
   * 리뷰 선택 핸들러 - 리뷰 선택 시 해당 리뷰의 모든 이미지도 자동 선택
   */
  const handleReviewSelect = (reviewId: number) => {
    const review = reviews.find((r) => r.id === reviewId);
    if (!review) return;

    // 기본 리뷰 선택 처리
    onReviewSelect(reviewId);

    // 해당 리뷰의 모든 이미지 자동 선택/해제
    const reviewImages = [review.beforeImageUrl, review.afterImageUrl].filter(
      Boolean,
    ) as string[];

    if (reviewImages.length > 0) {
      const isReviewSelected = selectedReviews.includes(reviewId);
      
      // 리뷰 선택 시 비호환 이미지 체크 및 알림
      if (!isReviewSelected) {
        const incompatibleImages = reviewImages.filter(imageUrl => {
          const compatibility = imageCompatibilityMap.get(imageUrl);
          return compatibility && !compatibility.isValid;
        });
        
        if (incompatibleImages.length > 0) {
          toast.warning(
            `⚠️ 선택된 리뷰에 Instagram 비호환 이미지 ${incompatibleImages.length}개가 포함되어 있습니다.`,
            { duration: 4000 }
          );
        }
      }

      reviewImages.forEach((imageUrl) => {
        const isImageSelected = selectedImages.includes(imageUrl);

        // 리뷰가 선택되고 이미지가 선택되지 않은 경우 -> 이미지 선택 (경고 건너뛰기)
        // 리뷰가 선택해제되고 이미지가 선택된 경우 -> 이미지 선택해제
        if (!isReviewSelected && !isImageSelected) {
          onImageSelect(imageUrl); // 리뷰 자동 선택 시 경고 없이 직접 선택
        } else if (isReviewSelected && isImageSelected) {
          onImageSelect(imageUrl); // 선택 해제는 호환성 체크 없이
        }
      });
    }
  };

  /**
   * 리뷰 카드 렌더링
   */
  const renderReviewCard = (review: Review) => {
    const isReviewSelected = selectedReviews.includes(review.id);
    const reviewImages = [review.beforeImageUrl, review.afterImageUrl].filter(
      Boolean,
    ) as string[];
    const selectedImageCount = reviewImages.filter((img) =>
      selectedImages.includes(img),
    ).length;

    return (
      <Grid item xs={12} sm={6} lg={4} key={review.id}>
        <Card
          sx={{
            borderRadius: 3,
            border: 2,
            borderColor: isReviewSelected ? 'primary.main' : 'divider',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: 4,
              borderColor: 'primary.light',
            },
            bgcolor: isReviewSelected ? 'primary.50' : 'background.paper',
          }}
          onClick={() => handleReviewSelect(review.id)}
        >
          <CardContent sx={{ p: 2.5 }}>
            {/* 헤더 */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>
                  {review.customerName?.[0] || '?'}
                </Avatar>
                <Box>
                  <Typography
                    variant='subtitle1'
                    fontWeight='700'
                    sx={{ lineHeight: 1.2 }}
                    style={{ color: '#1976d2', fontSize: '14px' }}
                  >
                    {review.customerName || '익명'}
                  </Typography>
                  <Typography
                    variant='caption'
                    style={{ color: '#666666', fontSize: '12px' }}
                  >
                    ID: {review.id}
                  </Typography>
                </Box>
              </Box>

              <Badge
                badgeContent={selectedImageCount}
                color='primary'
                invisible={selectedImageCount === 0}
              >
                <Checkbox
                  checked={isReviewSelected}
                  onChange={() => handleReviewSelect(review.id)}
                  icon={<RadioButtonUnchecked />}
                  checkedIcon={<CheckCircle />}
                  onClick={(e) => e.stopPropagation()}
                />
              </Badge>
            </Box>

            {/* 서비스 정보 */}
            <Box sx={{ mb: 2 }}>
              <Chip
                label={review.serviceType || '서비스'}
                size='small'
                color='primary'
                variant='outlined'
                icon={<Category sx={{ fontSize: 16 }} />}
                sx={{ mb: 1 }}
              />

              {review.menu && (
                <Typography
                  variant='body2'
                  sx={{ mb: 1 }}
                  style={{ color: '#424242', fontSize: '13px' }}
                >
                  📋 {review.menu}
                </Typography>
              )}
            </Box>

            {/* 리뷰 내용 */}
            {review.reviewText && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant='body2'
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    lineHeight: 1.4,
                    minHeight: '3.6em',
                  }}
                  style={{ color: '#212121', fontSize: '13px' }}
                >
                  {review.reviewText}
                </Typography>
              </Box>
            )}

            {/* 이미지 섹션 */}
            {reviewImages.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant='caption'
                  sx={{ mb: 1, display: 'block' }}
                  style={{ color: '#757575', fontSize: '11px' }}
                >
                  <ImageIcon
                    sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }}
                  />
                  이미지 {reviewImages.length}개 ({selectedImageCount}개 선택됨)
                </Typography>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  {review.beforeImageUrl && (
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: 2,
                        borderColor: selectedImages.includes(
                          review.beforeImageUrl,
                        )
                          ? 'success.main'
                          : 'divider',
                        cursor: 'pointer',
                        position: 'relative',
                        '&:hover': {
                          borderColor: 'success.light',
                          transform: 'scale(1.05)',
                        },
                        transition: 'all 0.2s',
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleImageSelect(review.beforeImageUrl!, false); // 개별 클릭 시 경고 표시
                      }}
                    >
                      <img
                        src={review.beforeImageUrl}
                        alt='식전'
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          bgcolor: 'rgba(0,0,0,0.7)',
                          px: 0.5,
                          py: 0.25,
                          fontSize: '0.6rem',
                          textAlign: 'center',
                        }}
                        style={{ color: 'white' }}
                      >
                        식전
                      </Box>
                      {/* 선택 상태 표시 */}
                      {selectedImages.includes(review.beforeImageUrl) && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 2,
                            right: 2,
                            bgcolor: 'success.main',
                            color: 'white',
                            borderRadius: '50%',
                            width: 16,
                            height: 16,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 2,
                          }}
                        >
                          <CheckCircle sx={{ fontSize: 12 }} />
                        </Box>
                      )}
                      
                      {/* Instagram 호환성 상태 표시 */}
                      {(() => {
                        const compatibility = imageCompatibilityMap.get(review.beforeImageUrl!);
                        const isChecking = checkingImages.has(review.beforeImageUrl!);
                        
                        if (isChecking) {
                          return (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 2,
                                left: 2,
                                bgcolor: 'rgba(33, 150, 243, 0.9)',
                                borderRadius: '4px',
                                px: 0.5,
                                py: 0.25,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <CircularProgress size={12} sx={{ color: 'white' }} />
                            </Box>
                          );
                        }
                        
                        if (compatibility) {
                          return (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 2,
                                left: 2,
                                bgcolor: compatibility.isValid ? 'rgba(76, 175, 80, 0.9)' : 'rgba(255, 152, 0, 0.9)',
                                borderRadius: '4px',
                                px: 0.5,
                                py: 0.25,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px solid white',
                              }}
                              title={compatibility.isValid ? 'Instagram 호환 가능' : `Instagram 비호환: ${compatibility.ratio}`}
                            >
                              {compatibility.isValid ? (
                                <CheckCircleOutline sx={{ fontSize: 16, color: 'white' }} />
                              ) : (
                                <Warning sx={{ fontSize: 16, color: 'white' }} />
                              )}
                            </Box>
                          );
                        }
                        
                        return null;
                      })()}
                    </Box>
                  )}

                  {review.afterImageUrl && (
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: 2,
                        borderColor: selectedImages.includes(
                          review.afterImageUrl,
                        )
                          ? 'success.main'
                          : 'divider',
                        cursor: 'pointer',
                        position: 'relative',
                        '&:hover': {
                          borderColor: 'success.light',
                          transform: 'scale(1.05)',
                        },
                        transition: 'all 0.2s',
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleImageSelect(review.afterImageUrl!, false); // 개별 클릭 시 경고 표시
                      }}
                    >
                      <img
                        src={review.afterImageUrl}
                        alt='식후'
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          bgcolor: 'rgba(0,0,0,0.7)',
                          px: 0.5,
                          py: 0.25,
                          fontSize: '0.6rem',
                          textAlign: 'center',
                        }}
                        style={{ color: 'white' }}
                      >
                        식후
                      </Box>
                      {/* 선택 상태 표시 */}
                      {selectedImages.includes(review.afterImageUrl) && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 2,
                            right: 2,
                            bgcolor: 'success.main',
                            color: 'white',
                            borderRadius: '50%',
                            width: 16,
                            height: 16,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 2,
                          }}
                        >
                          <CheckCircle sx={{ fontSize: 12 }} />
                        </Box>
                      )}
                      
                      {/* Instagram 호환성 상태 표시 */}
                      {(() => {
                        const compatibility = imageCompatibilityMap.get(review.afterImageUrl!);
                        const isChecking = checkingImages.has(review.afterImageUrl!);
                        
                        if (isChecking) {
                          return (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 2,
                                left: 2,
                                bgcolor: 'rgba(33, 150, 243, 0.9)',
                                borderRadius: '4px',
                                px: 0.5,
                                py: 0.25,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <CircularProgress size={12} sx={{ color: 'white' }} />
                            </Box>
                          );
                        }
                        
                        if (compatibility) {
                          return (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 2,
                                left: 2,
                                bgcolor: compatibility.isValid ? 'rgba(76, 175, 80, 0.9)' : 'rgba(255, 152, 0, 0.9)',
                                borderRadius: '4px',
                                px: 0.5,
                                py: 0.25,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px solid white',
                              }}
                              title={compatibility.isValid ? 'Instagram 호환 가능' : `Instagram 비호환: ${compatibility.ratio}`}
                            >
                              {compatibility.isValid ? (
                                <CheckCircleOutline sx={{ fontSize: 16, color: 'white' }} />
                              ) : (
                                <Warning sx={{ fontSize: 16, color: 'white' }} />
                              )}
                            </Box>
                          );
                        }
                        
                        return null;
                      })()}
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            <Divider sx={{ my: 1.5 }} />

            {/* 하단 정보 */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarToday sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography
                  variant='caption'
                  style={{ color: '#757575', fontSize: '11px' }}
                >
                  {formatDate(review.createdAt)}
                </Typography>
              </Box>

              {review.customerPhone && (
                <Typography
                  variant='caption'
                  style={{ color: '#757575', fontSize: '11px' }}
                >
                  📞 {review.customerPhone.slice(-4)}
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  return (
    <Box>
      {/* 헤더 */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant='h6'
            fontWeight='700'
            style={{ color: '#1976d2', fontSize: '18px' }}
          >
            📝 리뷰 선택 ({selectedReviews.length}개 선택됨)
          </Typography>
          <Typography
            variant='body2'
            style={{ color: '#666666', fontSize: '14px' }}
          >
            총 {totalCount}개의 리뷰 • {selectedImages.length}개 이미지 선택됨
            {(() => {
              const compatibleCount = Array.from(imageCompatibilityMap.values()).filter(c => c.isValid).length;
              const incompatibleCount = Array.from(imageCompatibilityMap.values()).filter(c => !c.isValid).length;
              const checkingCount = checkingImages.size;
              const totalChecked = compatibleCount + incompatibleCount;
              
              console.log('📊 Instagram 호환성 현황:', {
                totalChecked,
                compatibleCount,
                incompatibleCount,
                checkingCount,
                mapSize: imageCompatibilityMap.size
              });
              
              if (checkingCount > 0 || totalChecked > 0) {
                return (
                  <span style={{ marginLeft: '8px', fontWeight: 'bold' }}>
                    • 📸 Instagram 호환성: 
                    {compatibleCount > 0 && <span style={{ color: '#4caf50', backgroundColor: '#e8f5e8', padding: '2px 6px', borderRadius: '4px', marginLeft: '4px' }}> ✅{compatibleCount}개 호환</span>}
                    {incompatibleCount > 0 && <span style={{ color: '#ff9800', backgroundColor: '#fff3e0', padding: '2px 6px', borderRadius: '4px', marginLeft: '4px' }}> ⚠️{incompatibleCount}개 비호환</span>}
                    {checkingCount > 0 && <span style={{ color: '#2196f3', backgroundColor: '#e3f2fd', padding: '2px 6px', borderRadius: '4px', marginLeft: '4px' }}> 🔄{checkingCount}개 확인중</span>}
                  </span>
                );
              }
              return <span style={{ marginLeft: '8px', color: '#999' }}>• 📸 Instagram 호환성 확인 중...</span>;
            })()}
          </Typography>
        </Box>
      </Box>

      {/* Instagram 호환성 도움말 */}
      {Array.from(imageCompatibilityMap.values()).some(c => !c.isValid) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            ⚠️ <strong>Instagram 비호환 이미지 발견!</strong><br />
            일부 이미지가 Instagram에서 지원하지 않는 화면 비율입니다. 
            이미지 좌측 하단의 <Warning sx={{ fontSize: 14, verticalAlign: 'middle', mx: 0.5 }} /> 아이콘을 확인하세요.<br />
            <strong>Instagram 지원 비율:</strong> 정사각형(1:1), 세로(4:5), 가로(1.91:1)
          </Typography>
        </Alert>
      )}

      {/* 필터 섹션 - index.tsx와 동일한 스타일 적용 */}
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          marginBottom: '16px',
          padding: '16px',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '16px',
          }}
        >
          {/* 필터 제목 */}
          <div style={{ display: 'flex', flexShrink: 0 }}>
            <Typography
              variant='subtitle1'
              sx={{ fontWeight: 600 }}
              style={{ color: '#1976d2', fontSize: '16px' }}
            >
              📋 필터
            </Typography>
          </div>

          {/* 검색 */}
          <div style={{ flex: 1, minWidth: '200px', maxWidth: '350px' }}>
            <div style={{ position: 'relative' }}>
              <Search
                style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#9ca3af',
                  fontSize: '18px',
                }}
              />
              <input
                type='text'
                placeholder='리뷰 내용, 고객명 검색...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  paddingLeft: '40px',
                  paddingRight: '16px',
                  paddingTop: '8px',
                  paddingBottom: '8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: '#212121',
                  height: '40px',
                  outline: 'none',
                }}
              />
            </div>
          </div>

          {/* 기간 필터 */}
          <div style={{ flexShrink: 0, width: '140px' }}>
            <select
              value={dateRangeFilter}
              onChange={(e) => setDateRangeFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                backgroundColor: 'white',
                fontSize: '14px',
                color: '#212121',
                height: '40px',
                outline: 'none',
              }}
            >
              <option value='1' style={{ color: '#212121' }}>
                오늘
              </option>
              <option value='7' style={{ color: '#212121' }}>
                최근 7일
              </option>
              <option value='30' style={{ color: '#212121' }}>
                최근 30일
              </option>
              <option value='90' style={{ color: '#212121' }}>
                최근 90일
              </option>
              <option value='all' style={{ color: '#212121' }}>
                전체
              </option>
            </select>
          </div>

          {/* 초기화 버튼 */}
          <div style={{ flexShrink: 0 }}>
            <button
              onClick={handleResetFilters}
              style={{
                padding: '8px 16px',
                border: '1px solid #2563eb',
                color: '#2563eb',
                borderRadius: '8px',
                backgroundColor: 'white',
                fontSize: '14px',
                height: '40px',
                fontWeight: '500',
                whiteSpace: 'nowrap',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = '#eff6ff';
                e.currentTarget.style.borderColor = '#1d4ed8';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'white';
                e.currentTarget.style.borderColor = '#2563eb';
              }}
            >
              🔄 초기화
            </button>
          </div>
        </div>
      </div>

      {/* 리뷰 목록 */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={40} />
        </Box>
      ) : reviews.length === 0 ? (
        <Card sx={{ borderRadius: 3, textAlign: 'center', py: 6 }}>
          <CardContent>
            <Person sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography
              variant='h6'
              style={{ color: '#757575', fontSize: '16px' }}
            >
              조건에 맞는 리뷰가 없습니다
            </Typography>
            <Typography
              variant='body2'
              style={{ color: '#9e9e9e', fontSize: '14px' }}
            >
              필터 조건을 변경해보세요
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          <Grid container spacing={3}>
            {reviews.map(renderReviewCard)}
          </Grid>

          {/* 페이지네이션 */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color='primary'
                size='large'
                showFirstButton
                showLastButton
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontSize: '14px',
                    color: '#666666',
                    '&.Mui-selected': {
                      backgroundColor: '#1976d2',
                      color: 'white',
                    },
                  },
                }}
              />
            </Box>
          )}
        </>
      )}

      {/* 선택 요약 */}
      {(selectedReviews.length > 0 || selectedImages.length > 0) && (
        <Paper sx={{ mt: 4, p: 3, borderRadius: 3, bgcolor: 'primary.50' }}>
          <Typography
            variant='h6'
            fontWeight='700'
            sx={{ mb: 2 }}
            style={{ color: '#1976d2', fontSize: '16px' }}
          >
            🎯 선택 요약
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Alert severity='info' sx={{ borderRadius: 2 }}>
                <Typography
                  variant='subtitle2'
                  fontWeight='600'
                  style={{ color: '#0288d1', fontSize: '14px' }}
                >
                  선택된 리뷰: {selectedReviews.length}개
                </Typography>
                <Typography
                  variant='body2'
                  style={{ color: '#424242', fontSize: '13px' }}
                >
                  선택된 리뷰의 내용이 AI 포스팅 생성에 활용됩니다
                </Typography>
              </Alert>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Alert severity='success' sx={{ borderRadius: 2 }}>
                <Typography
                  variant='subtitle2'
                  fontWeight='600'
                  style={{ color: '#2e7d32', fontSize: '14px' }}
                >
                  선택된 이미지: {selectedImages.length}개
                </Typography>
                <Typography
                  variant='body2'
                  style={{ color: '#424242', fontSize: '13px' }}
                >
                  선택된 이미지가 포스팅에 포함됩니다
                </Typography>
              </Alert>
            </Grid>

            <Grid item xs={12} sm={4}>
              {(() => {
                // 선택된 이미지들의 Instagram 호환성 분석
                const selectedImageCompatibility = selectedImages.map(imageUrl => 
                  imageCompatibilityMap.get(imageUrl)
                ).filter(Boolean);
                
                const selectedCompatibleCount = selectedImageCompatibility.filter(c => c!.isValid).length;
                const selectedIncompatibleCount = selectedImageCompatibility.filter(c => !c!.isValid).length;
                const selectedCheckingCount = selectedImages.filter(imageUrl => 
                  checkingImages.has(imageUrl)
                ).length;
                
                const hasIncompatible = selectedIncompatibleCount > 0;
                const allChecked = selectedImageCompatibility.length === selectedImages.length;
                
                return (
                  <Alert 
                    severity={hasIncompatible ? 'warning' : (allChecked ? 'success' : 'info')} 
                    sx={{ borderRadius: 2 }}
                  >
                    <Typography
                      variant='subtitle2'
                      fontWeight='600'
                      style={{ 
                        color: hasIncompatible ? '#ed6c02' : (allChecked ? '#2e7d32' : '#0288d1'), 
                        fontSize: '14px' 
                      }}
                    >
                      📸 Instagram 호환성
                    </Typography>
                    <Typography
                      variant='body2'
                      style={{ color: '#424242', fontSize: '13px' }}
                    >
                      {selectedCheckingCount > 0 && `🔄 ${selectedCheckingCount}개 확인중 `}
                      {selectedCompatibleCount > 0 && `✅ ${selectedCompatibleCount}개 호환 `}
                      {selectedIncompatibleCount > 0 && `⚠️ ${selectedIncompatibleCount}개 비호환`}
                      {selectedImages.length === 0 && '이미지를 선택해주세요'}
                      {selectedImages.length > 0 && selectedImageCompatibility.length === 0 && '호환성 확인 중...'}
                    </Typography>
                    {hasIncompatible && (
                      <Typography
                        variant='caption'
                        style={{ color: '#ed6c02', fontSize: '12px', display: 'block', marginTop: '4px' }}
                      >
                        비호환 이미지는 Instagram 포스팅 시 오류 발생 가능
                      </Typography>
                    )}
                  </Alert>
                );
              })()}
            </Grid>
          </Grid>
        </Paper>
      )}
    </Box>
  );
};

export default ReviewSelector;
